import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, ChevronLeft, ChevronRight, Star, Clock, Heart } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { isGameFavorited, isGameLiked } from '@/services/likesService';
import { useNavigate } from 'react-router-dom';

interface GameRelease {
  id: string;
  title: string;
  cover_image_url?: string;
  release_date: string;
  price?: number;
  is_wishlisted?: boolean;
  is_liked?: boolean;
  developer_name?: string;
  tags?: string[];
}

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState<GameRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentQuarter, setCurrentQuarter] = useState(new Date());
  const [filter, setFilter] = useState<'all' | 'wishlist'>('all');

  useEffect(() => {
    if (user?.id) {
      loadGames();
    }
  }, [currentQuarter, user?.id]);

  const loadGames = async () => {
    setLoading(true);
    try {
      // Load 3 months of games
      const startOfQuarter = new Date(currentQuarter.getFullYear(), currentQuarter.getMonth(), 1);
      const endOfQuarter = new Date(currentQuarter.getFullYear(), currentQuarter.getMonth() + 3, 0);
      
      const { data, error } = await supabase
        .from('games')
        .select(`
          id,
          title,
          cover_image_url,
          release_date,
          price,
          genre,
          developer:profiles(display_name)
        `)
        .gte('release_date', startOfQuarter.toISOString().split('T')[0])
        .lte('release_date', endOfQuarter.toISOString().split('T')[0])
        .eq('is_published', true)
        .order('release_date', { ascending: true });

      if (error) {
        console.error('Error loading games:', error);
        return;
      }

      // Load wishlist and like status for each game if user is logged in
      const gamesWithReleases = await Promise.all(
        (data || []).map(async (game) => {
          let isWishlisted = false;
          let isLiked = false;
          
          if (user) {
            try {
              const [wishlistStatus, likeStatus] = await Promise.all([
                isGameFavorited(game.id, user.id),
                isGameLiked(game.id, user.id)
              ]);
              isWishlisted = wishlistStatus;
              isLiked = likeStatus;
            } catch (error) {
              console.error('Error checking game status for game:', game.id, error);
            }
          }
          
          return {
            id: game.id,
            title: game.title,
            cover_image_url: game.cover_image_url,
            release_date: game.release_date,
            price: game.price,
            is_wishlisted: isWishlisted,
            is_liked: isLiked,
            developer_name: game.developer?.display_name,
            tags: game.genre 
              ? (Array.isArray(game.genre) 
                  ? game.genre 
                  : game.genre.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0))
              : ['Action', 'Indie', 'Adventure'] // Parse comma-separated string or use defaults
          };
        })
      );

      setGames(gamesWithReleases);
    } catch (error) {
      console.error('Error loading games:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateQuarter = (direction: 'prev' | 'next') => {
    setCurrentQuarter(prev => {
      const newQuarter = new Date(prev);
      if (direction === 'prev') {
        newQuarter.setMonth(prev.getMonth() - 3);
      } else {
        newQuarter.setMonth(prev.getMonth() + 3);
      }
      return newQuarter;
    });
  };

  const goToCurrentMonth = () => {
    setCurrentQuarter(new Date());
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getGamesForDate = (date: Date) => {
    // Use local date to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    let filteredGames = games.filter(game => game.release_date === dateString);
    
    if (filter === 'wishlist') {
      filteredGames = filteredGames.filter(game => game.is_wishlisted);
    }
    
    return filteredGames;
  };

  const getGamesForMonth = (month: Date) => {
    const startOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
    const endOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    
    let monthGames = games.filter(game => {
      // Parse the date string directly to avoid timezone issues
      const [year, monthStr, dayStr] = game.release_date.split('-');
      const gameDate = new Date(parseInt(year), parseInt(monthStr) - 1, parseInt(dayStr));
      return gameDate >= startOfMonth && gameDate <= endOfMonth;
    });
    
    if (filter === 'wishlist') {
      monthGames = monthGames.filter(game => game.is_wishlisted);
    }
    
    return monthGames;
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    }).toUpperCase();
  };

  const formatDay = (date: Date) => {
    return date.getDate();
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getThreeMonths = () => {
    const months = [];
    for (let i = 0; i < 3; i++) {
      const month = new Date(currentQuarter.getFullYear(), currentQuarter.getMonth() + i, 1);
      months.push(month);
    }
    return months;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="grid grid-cols-7 gap-4">
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="h-32 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const weekDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const threeMonths = getThreeMonths();

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-bold">YOUR PERSONAL CALENDAR</h1>
          </div>
          <p className="text-xl text-muted-foreground">
            A 100% personalized list of games just for you, with recent and upcoming releases.
          </p>
        </div>

        {/* Quarter Navigation and Filter */}
        <div className="mb-6">
          {/* Mobile Layout */}
          <div className="block md:hidden space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-semibold">
                {formatMonthYear(currentQuarter)} - {formatMonthYear(new Date(currentQuarter.getFullYear(), currentQuarter.getMonth() + 2, 1))}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {games.length} game{games.length !== 1 ? 's' : ''} {filter === 'wishlist' ? 'in wishlist' : 'releasing'}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateQuarter('prev')}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Previous</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={goToCurrentMonth}
                className="flex items-center gap-1"
              >
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">Current Month</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateQuarter('next')}
                className="flex items-center gap-1"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex justify-center">
              <Select value={filter} onValueChange={(value: 'all' | 'wishlist') => setFilter(value)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Games</SelectItem>
                  <SelectItem value="wishlist">Wishlist Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateQuarter('prev')}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={goToCurrentMonth}
                className="flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                Current Month
              </Button>
            </div>
            
            <div className="text-center">
              <h2 className="text-2xl font-semibold">
                {formatMonthYear(currentQuarter)} - {formatMonthYear(new Date(currentQuarter.getFullYear(), currentQuarter.getMonth() + 2, 1))}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {games.length} game{games.length !== 1 ? 's' : ''} {filter === 'wishlist' ? 'in wishlist' : 'releasing'}
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <Select value={filter} onValueChange={(value: 'all' | 'wishlist') => setFilter(value)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Games</SelectItem>
                  <SelectItem value="wishlist">Wishlist Only</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateQuarter('next')}
                className="flex items-center gap-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Desktop Calendar - Steam Style */}
        <div className="hidden md:block space-y-6">
          {threeMonths.map((month, monthIndex) => {
            const days = getDaysInMonth(month);
            const monthGames = getGamesForMonth(month);
            
            return (
              <div key={monthIndex} className="bg-card rounded-custom-paddings border">
                {/* Month Header - Steam Style */}
                <div className="bg-muted/50 px-4 py-3 border-b rounded-custom-paddings mt-2 mx-2 mb-4">
                  <h3 className="text-lg font-semibold text-center">
                    {formatMonthYear(month)}
                  </h3>
                </div>
                
                {/* Week Days Header - Steam Style */}
                <div className="grid grid-cols-7 bg-muted/30">
                  {weekDays.map((day) => (
                    <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2 px-1">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days - Steam Style */}
                <div className="grid grid-cols-7">
                  {days.map((date, index) => {
                    if (!date) {
                      return <div key={index} className="min-h-32 bg-muted/20 border-r border-b border-border/50"></div>;
                    }

                    const dayGames = getGamesForDate(date);
                    const isCurrentDay = isToday(date);
                    const isPastDay = isPast(date);

                    return (
                      <div
                        key={index}
                        className={`min-h-32 border-r border-b border-border/50 p-1 ${
                          isCurrentDay 
                            ? 'bg-primary/15' 
                            : isPastDay 
                              ? 'bg-muted/20' 
                              : 'bg-background hover:bg-muted/30'
                        }`}
                      >
                        <div className="flex flex-col h-full">
                          {/* Day Number */}
                          <div className={`text-xs font-medium mb-1 ${
                            isCurrentDay ? 'text-primary font-bold' : 'text-foreground'
                          }`}>
                            {formatDay(date)}
                          </div>
                          
                          {/* Games List - Column Layout */}
                          <div className="flex-1 space-y-1">
                            {dayGames.map((game) => (
                              <div
                                key={game.id}
                                className="bg-muted/80 rounded p-1 hover:bg-muted transition-colors cursor-pointer group"
                                title={game.title}
                                onClick={() => navigate(`/game/${game.id}`)}
                              >
                                {/* Game Cover - Full Width */}
                                <div className="w-full h-20 bg-muted rounded mb-1">
                                  {game.cover_image_url ? (
                                    <img
                                      src={game.cover_image_url}
                                      alt={game.title}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Calendar className="w-6 h-6 text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                                
                                {/* Game Title */}
                                <div className="text-sm font-medium truncate group-hover:text-primary transition-colors mb-1">
                                  {game.title}
                                </div>
                                
                                {/* Tags */}
                                <div className="flex flex-wrap gap-1 mb-1">
                                  {Array.isArray(game.tags) && game.tags.slice(0, 4).map((tag, tagIndex) => (
                                    <span 
                                      key={tagIndex}
                                      className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded-full border border-primary/30"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                                
                                {/* Wishlist and Like indicators */}
                                <div className="flex items-center justify-center gap-1 mt-1">
                                  {game.is_wishlisted && (
                                    <Star className="w-3 h-3 text-yellow-500 fill-current" />
                                  )}
                                  {game.is_liked && (
                                    <Heart className="w-3 h-3 text-red-500 fill-current" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile List - Steam Style */}
        <div className="block md:hidden space-y-4">
          {threeMonths.map((month, monthIndex) => {
            const monthGames = getGamesForMonth(month);
            
            if (monthGames.length === 0) return null;
            
            return (
              <div key={monthIndex} className="space-y-3">
                {/* Month Header */}
                <div className="bg-muted/50 px-4 py-2 rounded-custom-paddings">
                  <h3 className="text-lg font-semibold text-center">
                    {formatMonthYear(month)}
                  </h3>
                </div>
                
                {/* Games List */}
                <div className="space-y-2">
                  {monthGames.map((game) => {
                    const releaseDate = new Date(game.release_date);
                    const dayName = releaseDate.toLocaleDateString('en-US', { weekday: 'long' });
                    const dayNumber = releaseDate.getDate();
                    
                    return (
                      <div 
                        key={game.id} 
                        className="bg-card rounded-custom-paddings border hover:bg-muted/50 transition-colors cursor-pointer group"
                        onClick={() => navigate(`/game/${game.id}`)}
                      >
                        <div className="flex gap-3 p-3">
                          {/* Game Cover */}
                          <div className="w-16 h-20 bg-muted rounded flex-shrink-0">
                            {game.cover_image_url ? (
                              <img
                                src={game.cover_image_url}
                                alt={game.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Calendar className="w-6 h-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          
                          {/* Game Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="font-semibold text-sm truncate group-hover:text-primary transition-colors">
                                {game.title}
                              </h4>
                              <div className="flex items-center gap-1 ml-2">
                                {game.is_wishlisted && (
                                  <Star className="w-4 h-4 text-yellow-500 fill-current flex-shrink-0" />
                                )}
                                {game.is_liked && (
                                  <Heart className="w-4 h-4 text-red-500 fill-current flex-shrink-0" />
                                )}
                                {/* Debug: Show status */}
                                <div className="text-xs text-muted-foreground">
                                  {game.is_wishlisted ? 'W' : ''}{game.is_liked ? 'L' : ''}
                                </div>
                              </div>
                            </div>
                            
                            <div className="text-xs text-muted-foreground mb-2">
                              {dayName}, {dayNumber} {formatMonthYear(month).split(' ')[0]}
                            </div>
                            
                            {/* Tags */}
                            <div className="flex flex-wrap gap-1">
                              {Array.isArray(game.tags) && game.tags.slice(0, 3).map((tag, tagIndex) => (
                                <span 
                                  key={tagIndex}
                                  className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full border border-primary/30"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>
    </Layout>
  );
}
